<script setup lang="ts">
import type { DischargeType } from '@/types/DischargeRequest';
import { dischargeRequestTypeSchema } from '@/validation/schemas/v1/discharge-request-type';
import { useForm } from 'vee-validate';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';
import { computed, watch } from 'vue';
import { useDischargeRequestProcess } from '@/helpers/useDischargeRequestProcess';

const props = withDefaults(
  defineProps<{
    modelValue: DischargeType | null;
  }>(),
  {},
);

const dischargeTypeOptions: Record<DischargeType, string> = {
  ['full']: 'Full discharge',
  ['partial']: 'Partial payment',
};

const emit = defineEmits<{
  (e: 'update:modelValue', v: DischargeType | null): void;
}>();

const { type } = useDischargeRequestProcess();

const { handleSubmit, setFieldValue, resetForm } = useForm({
  validationSchema: dischargeRequestTypeSchema,
  validateOnMount: false,
});

const readonly = computed(() => {
  return type.value === 'review';
});

watch(
  () => props.modelValue,
  (dischargeType) => {
    const values = { dischargeType };
    resetForm({ values });
  },
  { immediate: true },
);

const update = handleSubmit(
  (values) => {
    setFieldValue('dischargeType', values.dischargeType);
    emit('update:modelValue', values.dischargeType);
    return true;
  },
  () => {
    forceScrollToTop(0);
  },
);

defineExpose({ update });
</script>

<template>
  <div class="mb-0 mt-6">
    <BxField v-slot="{ field }" name="dischargeType">
      <BxSelect
        v-bind="field"
        label="Discharge type"
        label-type="floating"
        :list-items="dischargeTypeOptions"
        class="form-control"
        :disabled="readonly"
      />
    </BxField>
  </div>
</template>
